import { ApiError } from '../i18n/errors';

export type UiV2Mode = 'disabled' | 'admins_only' | 'everyone';

export type UiV2State = {
  mode: UiV2Mode;
  canUseV2: boolean;
  pref: 'v1' | 'v2';
  effectiveVersion: 'v1' | 'v2';
};

export type User = {
  id: number;
  username: string;
  empireName: string | null;
  empireColor: string;
  /** URL publique du drapeau empire (/uploads/flags/…), si uploadé. */
  empireFlagUrl?: string | null;
  needsEmpireName: boolean;
  tutorialSeen: boolean;
  newsSeenVersion: number;
  reducedMotion: boolean;
  isAdmin: boolean;
  /** Présent après déploiement UI V2 — résolution serveur. */
  ui?: UiV2State;
};

export type MartialLaw = {
  active: boolean;
  expiresAt: string | null;
  remainingSeconds: number;
  governorUserId: number | null;
  canActivate: boolean;
  /** Territoire de merveille mondiale : loi martiale interdite. */
  worldWonderBlocked?: boolean;
  level: number;
  activeCount: number;
  maxActive: number;
  durationHours: number;
};

export type PlayerNotification = {
  id: number;
  type: string;
  title: string;
  message: string;
  territoryId: number | null;
  actorUserId: number | null;
  availableAt: string;
  readAt: string | null;
  createdAt: string;
  territoryName?: string | null;
  territorySlug?: string | null;
  centroid?: { lng: number; lat: number } | null;
  actorName?: string | null;
  actorEmpire?: string | null;
  actorColor?: string | null;
};

export type NotificationsList = {
  enabled: boolean;
  unreadCount: number;
  notifications: PlayerNotification[];
};


export type ChatPeer = {
  id: number;
  username: string;
  empireName: string | null;
  empireColor: string;
};

export type ChatMessage = {
  id: number;
  conversationId: number;
  senderId: number;
  body: string;
  createdAt: string;
  mine: boolean;
};

export type ChatConversation = {
  id: number;
  /** Admin only: npc_relay = joueur↔bot ; direct = conversation perso admin */
  kind?: 'npc_relay' | 'direct';
  peer: ChatPeer;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastSenderId: number | null;
  unread: boolean;
};

export type ChatConversationsResponse = {
  conversations: ChatConversation[];
  unreadCount: number;
};

export type ChatThreadResponse = {
  conversationId: number;
  peer: ChatPeer;
  messages: ChatMessage[];
  unread: boolean;
  /** Admin : fil miroir joueur↔bot — l’envoi part comme le bot */
  kind?: 'npc_relay' | 'direct';
  replyAsNpc?: boolean;
  relayPlayerId?: number | null;
};

export type Wallet = {
  charges: number;
  max: number;
  msUntilNext: number | null;
  intervalSeconds: number;
};

export type ZonesStatus = {
  count: number;
  max: number;
  overCap: boolean;
  baseMax: number;
  techId: string;
  techLevel: number;
  graceActive: boolean;
  graceEndsAt: string;
  graceEndsAtMs: number;
  secondsUntilGraceEnd: number;
};

export type AllianceUser = {
  id: number;
  username: string;
  empireName: string | null;
  empireColor: string;
  allianceId?: number;
  createdAt?: string;
  relation?: 'none' | 'allied' | 'incoming' | 'outgoing';
};

export type PlayerPublicCard = {
  id: number;
  username: string;
  empireName: string | null;
  empireColor: string;
  empireFlagUrl: string | null;
  createdAt: string | null;
  territories: number;
  governed: number;
  directPoints: number;
  alliesCount: number;
  zonesCount: number;
  topTerritory: { id: number; name: string; weight: number } | null;
  relation: 'none' | 'allied' | 'incoming' | 'outgoing';
  allianceId: number | null;
};

export type AllianceList = {
  allies: AllianceUser[];
  incoming: AllianceUser[];
  outgoing: AllianceUser[];
  incomingCount: number;
};

const SSO_TRIED = 'terra.ssoTried';
/** localStorage survives mobile refreshes better than sessionStorage. */
function ssoTriedGet(): boolean {
  try {
    return localStorage.getItem(SSO_TRIED) === '1' || sessionStorage.getItem(SSO_TRIED) === '1';
  } catch {
    return false;
  }
}
function ssoTriedSet(): void {
  try {
    localStorage.setItem(SSO_TRIED, '1');
    sessionStorage.setItem(SSO_TRIED, '1');
  } catch {
    /* private mode */
  }
}
function ssoTriedClear(): void {
  try {
    localStorage.removeItem(SSO_TRIED);
    sessionStorage.removeItem(SSO_TRIED);
  } catch {
    /* ignore */
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const body = data as { error?: string; code?: string };
    throw new ApiError(body.error || `Erreur ${res.status}`, {
      code: body.code,
      status: res.status,
      payload: data,
    });
  }
  return data as T;
}

export const api = {
  async me() {
    return apiFetch<{
      user: User | null;
      wallet: Wallet | null;
      zones: ZonesStatus | null;
      robotization?: { unlocked: boolean; enabled: boolean } | null;
      ui?: UiV2State;
    }>('auth.php?action=me');
  },

  async login(username: string, password: string) {
    const res = await apiFetch<{ user: User; wallet: Wallet }>('auth.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'login', username, password }),
    });
    ssoTriedClear();
    return res;
  },

  async register(username: string, email: string, password: string) {
    const res = await apiFetch<{ user: User; wallet: Wallet }>('auth.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'register', username, email, password }),
    });
    ssoTriedClear();
    return res;
  },

  async logout() {
    // Prevent silent SSO from immediately logging back in after explicit logout.
    ssoTriedSet();
    return apiFetch<{ ok: boolean }>('auth.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'logout' }),
    });
  },

  async setEmpire(empireName: string, empireColor: string) {
    return apiFetch<{ user: User }>('auth.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'set_empire', empireName, empireColor }),
    });
  },

  async updateProfile(patch: Partial<{
    empireName: string;
    empireColor: string;
    tutorialSeen: boolean;
    newsSeenVersion: number;
    reducedMotion: boolean;
    autoInvestAtMax: boolean;
  }>) {
    return apiFetch<{
      user: User;
      robotization?: { unlocked: boolean; enabled: boolean };
    }>('auth.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'update_profile', ...patch }),
    });
  },

  async uploadEmpireFlag(file: File) {
    const fd = new FormData();
    fd.append('action', 'upload');
    fd.append('flag', file);
    const res = await fetch('/api/empire_flags.php?action=upload', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const body = data as { error?: string; code?: string };
      throw new ApiError(body.error || `Erreur ${res.status}`, {
        code: body.code,
        status: res.status,
        payload: data,
      });
    }
    return data as {
      ok: boolean;
      flagUrl: string;
      user: User;
      poles: number[];
    };
  },

  async removeEmpireFlag() {
    return apiFetch<{ ok: boolean; flagUrl: null; user: User; poles: number[] }>(
      'empire_flags.php',
      { method: 'POST', body: JSON.stringify({ action: 'remove' }) },
    );
  },

  async empireFlagMine() {
    return apiFetch<{ flagUrl: string | null; poles: number[] }>('empire_flags.php?action=mine');
  },

  async setEmpireFlagPole(territoryId: number) {
    return apiFetch<{ ok: boolean; poles: number[] }>('empire_flags.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'set_pole', territoryId }),
    });
  },

  async clearEmpireFlagPole(territoryId: number) {
    return apiFetch<{ ok: boolean; poles: number[] }>('empire_flags.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'clear_pole', territoryId }),
    });
  },

  async empireFlagsGeojson(opts: {
    bbox: string;
    /** Seuil min km² de la zone du mât (param API historique minEmpireKm2). */
    minEmpireKm2?: number;
    limit?: number;
    signal?: AbortSignal;
  }) {
    const q = new URLSearchParams({ action: 'geojson', bbox: opts.bbox });
    if (opts.minEmpireKm2 != null) q.set('minEmpireKm2', String(opts.minEmpireKm2));
    if (opts.limit != null) q.set('limit', String(opts.limit));
    return apiFetch<{
      type: string;
      features: Array<{
        type: string;
        geometry: { type: 'Point'; coordinates: [number, number] };
        properties: {
          id: number;
          userId: number;
          flagUrl: string;
          iconId: string;
          /** Surface km² de la zone territoriale du mât. */
          zoneKm2: number;
          /** Alias (= zoneKm2) pour compat. */
          empireKm2: number;
          empireName: string | null;
          empireColor: string;
        };
      }>;
    }>(`empire_flags.php?${q}`, opts.signal ? { signal: opts.signal } : undefined);
  },

  async setRobotization(enabled: boolean) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      robotization?: { unlocked: boolean; enabled: boolean };
    }>('auth.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'set_robotization', enabled }),
    });
  },

  async changePassword(currentPassword: string, newPassword: string, newPasswordConfirm: string) {
    return apiFetch<{ ok: boolean }>('auth.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'change_password',
        currentPassword,
        newPassword,
        newPasswordConfirm,
      }),
    });
  },

  async ssoStart(next = '/', silent = false) {
    const q = new URLSearchParams({ action: 'sso_start', next });
    if (silent) q.set('silent', '1');
    return apiFetch<{ url: string }>(`auth.php?${q}`);
  },

  async trySilentSso(): Promise<'redirect' | 'skip'> {
    const url = new URL(window.location.href);
    if (url.searchParams.has('sso_silent') || url.searchParams.has('sso_failed')) {
      url.searchParams.delete('sso_silent');
      url.searchParams.delete('sso_failed');
      url.searchParams.delete('next');
      window.history.replaceState({}, '', url.pathname + url.search);
      ssoTriedSet();
      return 'skip';
    }
    if (ssoTriedGet()) return 'skip';
    const start = await api.ssoStart('/', true);
    if (!start.url) return 'skip';
    ssoTriedSet();
    window.location.href = start.url;
    return 'redirect';
  },

  async geojson(opts?: {
    bbox?: string;
    layer?: 'playable' | 'communes' | 'departments' | 'countries' | 'empires' | string;
    limit?: number;
    minEmpireKm2?: number;
    zoom?: number;
    signal?: AbortSignal;
  }) {
    const q = new URLSearchParams({ action: 'geojson' });
    if (opts?.bbox) q.set('bbox', opts.bbox);
    if (opts?.layer) q.set('layer', opts.layer);
    if (opts?.limit) q.set('limit', String(opts.limit));
    if (opts?.minEmpireKm2 != null) q.set('minEmpireKm2', String(opts.minEmpireKm2));
    if (opts?.zoom != null && Number.isFinite(opts.zoom)) {
      q.set('zoom', String(Math.round(opts.zoom * 10) / 10));
    }
    return apiFetch<{ type: string; features: Array<{ type: string; geometry: unknown; properties: Record<string, unknown> }>; meta?: Record<string, unknown> }>(
      `territories.php?${q}`,
      opts?.signal ? { signal: opts.signal } : undefined,
    );
  },

  async territory(id: number) {
    return apiFetch<{
      territory: Record<string, unknown>;
      influence: {
        owners: Array<Record<string, unknown>>;
        governorUserId: number | null;
        contested: boolean;
        viewer: Record<string, unknown> | null;
        investorCount: number;
        intelligence: {
          ok: boolean;
          target: { userId: number; username: string; empireName: string | null; empireColor: string };
          advantages: { military: number; economic: number };
          military: {
            tier: number;
            territoryId: number | null;
            directPoints?: number;
            investmentCount?: number;
            weight?: number;
            percent?: number;
            adjBonus?: number;
            networkBonus?: number;
            allianceBonus?: number;
            technologyBonus?: number;
            temporaryBonus?: number;
          };
          economic: {
            tier: number;
            approximate?: boolean;
            stocks?: Record<string, number>;
            hourlyRates?: Record<string, number>;
          };
        } | null;
      };
      neighbors: Array<{ id: number; name: string; type: string; strong: boolean }>;
      culturalCapital?: {
        isCapital: boolean;
        designatedAt: string | null;
        slots: number;
        used: number;
        canDesignate: boolean;
      } | null;
      favorite?: {
        unlocked: boolean;
        isFavorite: boolean;
        favoritedAt: string | null;
        lockedUntil: string | null;
        lockHours: number;
        used: number;
        slots: number;
        canAdd: boolean;
        canRemove: boolean;
        canToggle: boolean;
      } | null;
      pilgrimPaths?: {
        unlocked: boolean;
        slots: number;
        used: number;
        networkBonus?: number;
        paths: Array<{
          id: number;
          territoryA: number;
          territoryB: number;
          path: number[];
          pathLength: number;
          state: string;
          remainingSeconds?: number;
          costs?: Record<string, number>;
        }>;
      } | null;
      scenicRoutes?: {
        unlocked: boolean;
        slots: number;
        used: number;
        routes: Array<{
          id: number;
          territoryA: number;
          territoryB: number;
          path: number[];
          pathLength: number;
          state: string;
          costs?: Record<string, number>;
        }>;
      } | null;
      investors?: {
        owned: number;
        assigned: number;
        reserve: number;
        zoneAssigned: number;
        ecoBonusPercent: number;
        buildCutPercent: number;
        canAssign: boolean;
        canRemove: boolean;
        governs: boolean;
        inZone: boolean;
      } | null;
      canInvest?: boolean;
      canExpand: boolean;
      investBlockReason?:
        | 'ALLY_GOVERNOR'
        | 'TERRITORY_PAUSED'
        | 'MARTIAL_LAW'
        | 'MARTIAL_LAW_ACTIVE'
        | 'ZONE_LIMIT'
        | 'NEED_ADJACENT_GOVERNOR'
        | null;
      martialLaw: MartialLaw;
      worldWonder?: {
        id: string;
        nameFr: string;
        nameEn: string;
        bonusType: string;
        bonusPercent: number;
        image: string;
        countryCode: string;
        viewerIsGovernor: boolean;
        governorUserId: number | null;
      } | null;
      viewerPaused?: boolean;
      allyGovernor?: {
        id: number;
        username: string;
        empireName: string | null;
        empireColor: string;
      } | null;
      zones?: ZonesStatus | null;
      recover?: {
        available: boolean;
        charges: number;
        expiresAt: string | null;
      } | null;
    }>(`territories.php?action=get&id=${id}`);
  },

  async search(q: string) {
    return apiFetch<{ results: Array<{ id: number; name: string; centroid: { lng: number; lat: number } }> }>(
      `territories.php?action=search&q=${encodeURIComponent(q)}`,
    );
  },

  async myEmpire() {
    return apiFetch<{
      territories: Array<{
        id: number;
        name: string;
        slug: string;
        country: string | null;
        region: string | null;
        department: string | null;
        centroid: { lng: number; lat: number };
        areaKm2: number | null;
        directPoints: number;
        investmentCount: number;
        weight: number;
        percent: number;
        isGovernor: boolean;
        /** Autre gouverneur ; le joueur a une part d’influence. */
        isMinority: boolean;
        isContested: boolean;
        isPaused: boolean;
        zoneId: number | null;
        zoneSize: number | null;
      }>;
      zoneGroups: Array<{ zoneId: number; size: number; points: number; minTid: number }>;
      zones: ZonesStatus;
      stats: {
        count: number;
        governed: number;
        totalDirectPoints: number;
        totalWeight: number;
        zoneCount: number;
        zoneMax: number;
      };
    }>('territories.php?action=mine');
  },

  async abandonTerritory(territoryId: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      chargesRefunded?: number;
      emptied?: boolean;
      wallet?: Wallet;
      zones?: ZonesStatus;
      influencePending?: boolean;
      recalculationPending?: boolean;
    }>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({ mode: 'abandon', territoryId }),
    });
  },

  async recoverRecentInvestments(territoryId: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      chargesRefunded?: number;
      emptied?: boolean;
      wallet?: Wallet;
      zones?: ZonesStatus;
      influence?: unknown;
      recover?: { available: boolean; charges: number; expiresAt: string | null };
      influencePending?: boolean;
      recalculationPending?: boolean;
    }>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({ mode: 'recover_recent', territoryId }),
    });
  },

  async purgeZonesPreview() {
    return apiFetch<{
      ok: boolean;
      before: number;
      after: number;
      max: number;
      territories: number[];
      chargesRefunded: number;
      zonesRemoved: number;
    }>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({ mode: 'purge_zones_preview' }),
    });
  },

  async purgeZones() {
    return apiFetch<{
      ok: boolean;
      before: number;
      after: number;
      max: number;
      territories: number[];
      chargesRefunded: number;
      zonesRemoved: number;
      wallet?: Wallet;
      zones?: ZonesStatus;
      influencePending?: boolean;
      recalculationPending?: boolean;
    }>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({ mode: 'purge_zones', confirm: 'PURGE_SMALL_ZONES' }),
    });
  },

  async abandonBatchPreview(territoryIds: number[]) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      territories: number[];
      chargesRefunded: number;
      zones?: ZonesStatus;
    }>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({ mode: 'abandon_batch_preview', territoryIds }),
    });
  },

  async abandonBatch(territoryIds: number[]) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      territories: number[];
      chargesRefunded: number;
      wallet?: Wallet;
      zones?: ZonesStatus;
      influencePending?: boolean;
      recalculationPending?: boolean;
    }>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({
        mode: 'abandon_batch',
        territoryIds,
        confirm: 'ABANDON_BATCH',
      }),
    });
  },


  async invest(territoryId: number, idempotencyKey: string) {
    return apiFetch<Record<string, unknown>>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({ mode: 'invest', territoryId, idempotencyKey }),
    });
  },

  async activateMartialLaw(territoryId: number, idempotencyKey: string) {
    return apiFetch<{ ok: boolean; duplicate?: boolean; error?: string; code?: string } & Partial<MartialLaw>>("technologies.php", {
      method: "POST",
      body: JSON.stringify({ action: "activate_martial_law", territoryId, idempotencyKey }),
    });
  },

  async designateCulturalCapital(territoryId: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      culturalCapital?: {
        isCapital: boolean;
        designatedAt: string | null;
        slots: number;
        used: number;
        canDesignate: boolean;
      };
      influencePending?: boolean;
      recalculationPending?: boolean;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'designate_capital', territoryId }),
    });
  },

  async removeCulturalCapital(territoryId: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      culturalCapital?: {
        isCapital: boolean;
        designatedAt: string | null;
        slots: number;
        used: number;
        canDesignate: boolean;
      };
      influencePending?: boolean;
      recalculationPending?: boolean;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'remove_capital', territoryId }),
    });
  },

  async toggleFavoriteTerritory(territoryId: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      lockedUntil?: string;
      favorite?: {
        unlocked: boolean;
        isFavorite: boolean;
        favoritedAt: string | null;
        lockedUntil: string | null;
        lockHours: number;
        used: number;
        slots: number;
        canAdd: boolean;
        canRemove: boolean;
        canToggle: boolean;
      };
      influencePending?: boolean;
      recalculationPending?: boolean;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'toggle_favorite', territoryId }),
    });
  },

  async pilgrimPreview(territoryA: number, territoryB: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      path?: number[];
      length?: number;
      costs?: Record<string, number>;
      durationSeconds?: number;
      isReconsecration?: boolean;
      slots?: number;
      used?: number;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'pilgrim_preview', territoryA, territoryB }),
    });
  },

  async pilgrimConsecrate(territoryA: number, territoryB: number, idempotencyKey: string) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      path?: Record<string, unknown>;
      slots?: number;
      used?: number;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'pilgrim_consecrate',
        territoryA,
        territoryB,
        idempotencyKey,
      }),
    });
  },

  async pilgrimCancel(pathId: number) {
    return apiFetch<{ ok: boolean; error?: string; code?: string; slots?: number; used?: number }>(
      'technologies.php',
      {
        method: 'POST',
        body: JSON.stringify({ action: 'pilgrim_cancel', pathId }),
      },
    );
  },

  async scenicPreview(territoryA: number, territoryB: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      path?: number[];
      length?: number;
      costs?: Record<string, number>;
      slots?: number;
      used?: number;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'scenic_preview', territoryA, territoryB }),
    });
  },

  async scenicCreate(territoryA: number, territoryB: number, idempotencyKey: string) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      route?: Record<string, unknown>;
      slots?: number;
      used?: number;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'scenic_create',
        territoryA,
        territoryB,
        idempotencyKey,
      }),
    });
  },

  async scenicAbandon(routeId: number) {
    return apiFetch<{ ok: boolean; error?: string; code?: string; slots?: number; used?: number }>(
      'technologies.php',
      {
        method: 'POST',
        body: JSON.stringify({ action: 'scenic_abandon', routeId }),
      },
    );
  },

  async setTerritoryPaused(territoryId: number, paused: boolean) {
    return apiFetch<Record<string, unknown>>('conquest.php', {
      method: 'POST',
      body: JSON.stringify({ mode: paused ? 'pause' : 'resume', territoryId }),
    });
  },

  /** @deprecated alias */
  async conquerExpand(territoryId: number, idempotencyKey: string) {
    return this.invest(territoryId, idempotencyKey);
  },

  async geojsonAggregates(bbox?: string) {
    const q = new URLSearchParams({ action: 'geojson', layer: 'communes', limit: '3000' });
    if (bbox) q.set('bbox', bbox);
    return apiFetch<{ type: string; features: Array<{ type: string; geometry: unknown; properties: Record<string, unknown> }> }>(
      `territories.php?${q}`,
    );
  },

  async geojsonDepartments(bbox?: string) {
    const q = new URLSearchParams({ action: 'geojson', layer: 'departments', limit: '200' });
    if (bbox) q.set('bbox', bbox);
    return this.geojson({ bbox, layer: 'departments', limit: 200 });
  },

  async territoryStats() {
    return apiFetch<{ stats: Record<string, number> }>('territories.php?action=stats');
  },

  async leaderboard(scope = 'dominated', page = 1, resource?: string) {
    const params: Record<string, string> = { scope, page: String(page) };
    if (resource) {
      params.resource = resource;
    }
    const q = new URLSearchParams(params);
    return apiFetch<{
      formulaKey?: 'dominated' | 'population' | 'area' | 'production' | 'productionEmpty' | 'score';
      /** @deprecated texte FR legacy — préférer formulaKey */
      formula?: string;
      resource?: ResourceType;
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
      entries: Array<{
        rank: number;
        userId: number;
        username: string;
        empireName: string | null;
        empireColor: string;
        value: number;
      }>;
    }>(`leaderboard.php?${q}`);
  },

  /** Strongest territory by influence weight for any player (public). */
  async topInfluenceTerritory(userId: number) {
    return apiFetch<{
      territory: {
        id: number;
        name: string;
        slug: string;
        weight: number;
        centroid: { lng: number; lat: number };
      } | null;
    }>(`territories.php?action=top_influence&userId=${userId}`);
  },


  async chatConversations() {
    return apiFetch<ChatConversationsResponse>('chat.php?action=conversations');
  },

  async chatUnread() {
    return apiFetch<{ unreadCount: number }>('chat.php?action=unread');
  },

  async chatSearchUsers(query: string) {
    return apiFetch<{ results: ChatPeer[] }>(`chat.php?action=search&q=${encodeURIComponent(query)}`);
  },

  async chatMessages(userId: number) {
    return apiFetch<ChatThreadResponse>(`chat.php?action=messages&userId=${userId}`);
  },

  async chatSend(userId: number, body: string) {
    return apiFetch<ChatThreadResponse & ChatConversationsResponse & { ok: boolean; messageId?: number }>('chat.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'send', userId, body }),
    });
  },

  async chatHide(userId: number) {
    return apiFetch<ChatConversationsResponse & { ok: boolean }>('chat.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'hide', userId }),
    });
  },

  async events() {
    return apiFetch<{ events: Array<{ id: number; message: string; createdAt: string; color: string }> }>('events.php');
  },

  async notifications() {
    return apiFetch<NotificationsList>('notifications.php?action=list');
  },

  async markAllNotificationsRead() {
    return apiFetch<{ ok: boolean; updatedCount: number; unreadCount: number }>('notifications.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'mark_all_read' }),
    });
  },

  async deleteNotification(notificationId: number) {
    return apiFetch<{ ok: boolean; deletedId: number; unreadCount: number }>('notifications.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'delete', notificationId }),
    });
  },

  async alliances() {
    return apiFetch<AllianceList>('alliances.php?action=list');
  },

  async searchAllianceUsers(query: string) {
    return apiFetch<{ results: AllianceUser[] }>(`alliances.php?action=search&q=${encodeURIComponent(query)}`);
  },

  async playerCard(userId: number) {
    return apiFetch<{ player: PlayerPublicCard }>(`alliances.php?action=card&userId=${userId}`);
  },

  async allianceAction(action: 'send' | 'accept' | 'decline' | 'cancel' | 'break', payload: { userId?: number; allianceId?: number }) {
    return apiFetch<AllianceList & { ok: boolean }>('alliances.php', {
      method: 'POST',
      body: JSON.stringify({ action, ...payload }),
    });
  },

  async adminPlayers(query = '', opts?: { npcOnly?: boolean }) {
    const q = new URLSearchParams({ action: 'players' });
    if (query.trim()) q.set('q', query.trim());
    if (opts?.npcOnly) q.set('npc', '1');
    return apiFetch<{
      players: AdminPlayer[];
      total: number;
      viewerId: number;
      chargesMax: number;
      npcOnly?: boolean;
    }>(`admin.php?${q}`);
  },

  async adminAdjustCharges(userId: number, delta: number) {
    return apiFetch<{
      ok: boolean;
      userId: number;
      before: number;
      after: number;
      delta: number;
      wallet: Wallet;
    }>('admin.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'adjust_charges', userId, delta }),
    });
  },

  async adminUiV2Get() {
    return apiFetch<{ ok: boolean; ui: UiV2State; modes: UiV2Mode[] }>('admin.php?action=uiV2Get');
  },

  async adminUiV2SetMode(mode: UiV2Mode) {
    return apiFetch<{ ok: boolean; ui: UiV2State }>('admin.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'uiV2SetMode', mode }),
    });
  },

  async adminUiV2SetPref(pref: 'v1' | 'v2') {
    return apiFetch<{ ok: boolean; ui: UiV2State; user: User }>('admin.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'uiV2SetPref', pref }),
    });
  },

  async suggestionsSubmit(body: string, opts?: { territoryId?: number }) {
    return apiFetch<{ ok: boolean; id: number }>('suggestions.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'submit',
        body,
        ...(opts?.territoryId != null ? { territoryId: opts.territoryId } : {}),
      }),
    });
  },

  async suggestionsList(filter: SuggestionsFilter = 'all') {
    const q = new URLSearchParams({ action: 'list', filter });
    return apiFetch<{
      suggestions: PlayerSuggestion[];
      unreadCount: number;
      counts: SuggestionsCounts;
      filter: SuggestionsFilter;
    }>(`suggestions.php?${q}`);
  },

  async suggestionsUnread() {
    return apiFetch<{ unreadCount: number }>('suggestions.php?action=unread');
  },

  async suggestionsMarkRead(id: number) {
    return apiFetch<{ ok: boolean; unreadCount: number; counts: SuggestionsCounts }>('suggestions.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'mark_read', id }),
    });
  },

  async suggestionsMarkAllRead() {
    return apiFetch<{ ok: boolean; unreadCount: number; counts: SuggestionsCounts }>('suggestions.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'mark_all_read' }),
    });
  },

  async suggestionsArchive(id: number) {
    return apiFetch<{ ok: boolean; unreadCount: number; counts: SuggestionsCounts }>('suggestions.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'archive', id }),
    });
  },

  async suggestionsUnarchive(id: number) {
    return apiFetch<{ ok: boolean; unreadCount: number; counts: SuggestionsCounts }>('suggestions.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'unarchive', id }),
    });
  },

  async onuState() {
    return apiFetch<OnuState>('onu.php?action=state');
  },

  async onuVote(sessionId: number, choice: 'a' | 'b') {
    return apiFetch<{
      ok: boolean;
      vote: { choice: 'a' | 'b'; weight: number };
      voting: OnuSession | null;
      activeLaw: OnuActiveLaw | null;
      serverNow: string;
    }>('onu.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'vote', sessionId, choice }),
    });
  },
};

export type OnuLaw = {
  code: string;
  family: string;
  familyLabel: string;
  bonusAxis: string;
  malusAxis: string;
  percent: number;
  title: string;
  summary: string;
  bonusLabel: string;
  malusLabel: string;
};

export type OnuSession = {
  id: number;
  status: 'voting' | 'resolved' | 'tied';
  family: string;
  familyLabel: string;
  lawA: OnuLaw | null;
  lawB: OnuLaw | null;
  lawDurationHours: number;
  voteOpensAt: string;
  voteClosesAt: string;
  totalWeight: number;
  results: {
    weightA: number;
    weightB: number;
    percentA: number;
    percentB: number;
    participationPercent: number;
    weightCast?: number;
  };
  winnerChoice: 'a' | 'b' | null;
  effectStartsAt: string | null;
  effectEndsAt: string | null;
  me: {
    weight: number;
    rank: number | null;
    weightPercent: number;
    canVote: boolean;
    myVote: 'a' | 'b' | null;
  } | null;
};

export type OnuActiveLaw = {
  sessionId: number;
  law: OnuLaw;
  startsAt: string;
  endsAt: string;
};

export type OnuState = {
  serverNow: string;
  voting: OnuSession | null;
  activeLaw: OnuActiveLaw | null;
  voteHours: number;
  lawDurationOptions: number[];
};

export type SuggestionsFilter = 'all' | 'unread' | 'read' | 'archived';

export type SuggestionsCounts = {
  all: number;
  unread: number;
  read: number;
  archived: number;
};

export type PlayerSuggestion = {
  id: number;
  userId: number;
  username: string;
  empireName: string | null;
  empireColor: string;
  body: string;
  territoryId: number | null;
  territoryName: string | null;
  centroid: { lng: number; lat: number } | null;
  createdAt: string;
  readAt: string | null;
  archivedAt: string | null;
  unread: boolean;
  archived: boolean;
};

export type AdminPlayer = {
  id: number;
  username: string;
  empireName: string | null;
  empireColor: string;
  isAdmin: boolean;
  isNpc?: boolean;
  createdAt: string | null;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  charges: number;
  chargesMax: number;
  msUntilNext: number | null;
  territories: number;
  governed: number;
  zonesCount: number;
  zonesMax: number;
  directPoints: number;
  allyCount: number;
  allies: Array<{ id: number; username: string; empireName: string | null }>;
};

export function newIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── Points d'intérêt ────────────────────────────────────────────────────────

export type PoiCategory = 'science' | 'culture' | 'economy' | 'military' | 'spirituality';
export type PoiLevel    = 'local' | 'regional' | 'national' | 'world';

export type PoiFeature = {
  id: number;
  name: string;
  category: PoiCategory;
  level: PoiLevel;
  icon: string;
  country: string;
  baseYield: number;
  territoryId: number | null;
  territoryName: string | null;
  territorySlug: string | null;
  lng: number;
  lat: number;
};

export type PoiDetail = PoiFeature & {
  nameLocal: string | null;
  source: string;
  externalId: string | null;
  isValidated: boolean;
  territory: {
    id: number;
    name: string;
    slug: string;
    country: string | null;
    region: string | null;
    department: string | null;
  } | null;
};

export const poiApi = {
  async list(opts?: {
    bbox?: string;
    category?: PoiCategory;
    level?: PoiLevel;
    country?: string;
    territoryId?: number;
    limit?: number;
  }) {
    const q = new URLSearchParams({ action: 'list' });
    if (opts?.bbox)         q.set('bbox',        opts.bbox);
    if (opts?.category)     q.set('category',    opts.category);
    if (opts?.level)        q.set('level',       opts.level);
    if (opts?.country)      q.set('country',     opts.country);
    if (opts?.territoryId)  q.set('territoryId', String(opts.territoryId));
    if (opts?.limit)        q.set('limit',       String(opts.limit));
    return apiFetch<{
      type: 'FeatureCollection';
      features: Array<{ type: string; geometry: { type: string; coordinates: [number, number] }; properties: PoiFeature }>;
      meta: { count: number };
    }>(`poi.php?${q}`);
  },

  async get(id: number) {
    return apiFetch<{ poi: PoiDetail }>(`poi.php?action=get&id=${id}`);
  },

  async categories() {
    return apiFetch<{
      categories: Array<{ category: PoiCategory; count: number; icon: string }>;
    }>('poi.php?action=categories');
  },
};

export type WorldWonderMapFeature = {
  id: string;
  nameFr: string;
  nameEn: string;
  territoryId: number;
  countryCode: string;
  bonusType: string;
  bonusPercent: number;
  image: string;
};

export const worldWondersApi = {
  async geojson() {
    return apiFetch<{
      type: 'FeatureCollection';
      features: Array<{
        type: 'Feature';
        geometry: { type: 'Point'; coordinates: [number, number] };
        properties: WorldWonderMapFeature;
      }>;
      meta: { count: number; enabled: boolean };
    }>('world_wonders.php?action=geojson');
  },
};

// ─── Ressources & Production horaire (Étape 4) ────────────────────────────────

export type ResourceType = 'science' | 'culture' | 'economy' | 'military' | 'spirituality';

export type ResourceBalance = {
  resource: ResourceType;
  balance: number;
  lifetimeEarned: number;
  lifetimeSpent: number;
};

export type ProducingPoi = {
  poiId: number;
  name: string;
  resourceType: ResourceType;
  lng: number;
  lat: number;
  territoryId: number;
  territoryName: string;
  territorySlug: string;
  influencePercent: number;
  isGovernor: boolean;
  multiplier: number;
  baseYield: number;
  hourlyRate: number;
};

export type ResourceBreakdownTech = {
  id: string;
  name: string;
  percent: number;
  level: number;
  scope: 'global' | 'conditional' | string;
  activePoiCount: number | null;
};

export type ResourceBreakdownBuilding = {
  type: string;
  name: string;
  count: number;
  percent: number;
  scope: 'global' | 'conditional' | string;
  activePoiCount: number | null;
};

export type ResourceBreakdownOther = {
  id: string;
  name: string;
  percent: number;
  detail: string | null;
  /** Production absolue / h (ex. intérêts bancaires) — prioritaire sur percent si défini. */
  absoluteHourly?: number | null;
};

export type ResourceBreakdown = {
  poiCount: number;
  baseHourly: number;
  finalHourly: number;
  technologies: ResourceBreakdownTech[];
  buildings: ResourceBreakdownBuilding[];
  other: ResourceBreakdownOther[];
};

export type MaterialSummary = {
  balance: number;
  hourlyRate: number;
  buildingCount: number;
};

export type ResourceSummary = {
  balances: Record<ResourceType, ResourceBalance>;
  hourlyRates: Record<ResourceType, number>;
  producingPois: ProducingPoi[];
  producingPoiCount: number;
  breakdown?: Partial<Record<ResourceType, ResourceBreakdown>>;
  empire: {
    population: number;
    areaKm2: number;
    density: number;
    influencePercent: number;
    productionPercent: number;
    economyPopulationPercent: number;
  } | null;
  materials?: {
    steel: MaterialSummary;
    concrete: MaterialSummary;
  };
};

export type ResourceLedgerEntry = {
  id: number;
  resourceType: ResourceType;
  amount: number;
  entryType: 'production' | 'spend' | 'grant' | 'adjustment' | 'refund';
  referenceType: string;
  referenceId: number | null;
  idempotencyKey: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export type ResourceEngineState = {
  state: {
    lastCompletedPeriodEnd: string | null;
    maxCatchupHours: number;
    updatedAt: string | null;
  };
  lastRun: {
    id: number;
    periodStart: string;
    periodEnd: string;
    status: string;
    poiCount: number;
    producerCount: number;
    totalProduced: number;
    completedAt: string | null;
  } | null;
};

export const resourceApi = {
  async summary() {
    return apiFetch<ResourceSummary>('resources.php?action=summary');
  },

  async ledger(limit = 50) {
    return apiFetch<{ entries: ResourceLedgerEntry[] }>(`resources.php?action=ledger&limit=${limit}`);
  },

  async state() {
    return apiFetch<ResourceEngineState>('resources.php?action=state');
  },

  async dryRun() {
    return apiFetch<Record<string, unknown>>('resources.php?action=dry_run');
  },
};

// ─── Technologies ─────────────────────────────────────────────────────────────

export type TechnologyCategoryId = ResourceType;

export type TechnologyPurchaseKind = 'instant' | 'research' | 'action';

export type TechnologyNode = {
  id: string;
  name: string;
  description: string;
  category: TechnologyCategoryId;
  branch: string;
  icon: string;
  position: { x: number; y: number };
  enabled: boolean;
  baseCost: number;
  costMultiplier: number;
  maxLevel: number | null;
  repeatable: boolean;
  effect: { type: string; params: Record<string, unknown> };
  prerequisites: Array<{ technologyId: string; level: number }>;
  level: number;
  nextLevel: number | null;
  nextCost: number | null;
  nextCosts?: Record<string, number> | null;
  costAllCategories?: boolean;
  resource: TechnologyCategoryId;
  maxed: boolean;
  missingPrerequisites: Array<{
    technologyId: string;
    name?: string;
    requiredLevel: number;
    currentLevel: number;
  }>;
  purchasable: boolean;
  graceSuspended?: boolean;
  graceSuspendedMessage?: string | null;
  consumableQuantity: number;
  usableWithoutTarget: boolean;
  requiresTarget: boolean;
  /** ISO datetime (with offset) until which purchase is locked (consumables). */
  purchaseAvailableAt?: string | null;
  /** Seconds remaining until purchase unlock (server clock). */
  purchaseCooldownRemainingSeconds?: number | null;
  /** Configured purchase recharge in seconds (e.g. 3600 for economy consumables). */
  purchaseCooldownSeconds?: number | null;
  purchaseKind?: TechnologyPurchaseKind;
  adminOnly?: boolean;
  researchDurationSeconds?: number | null;
  researchDurationLabel?: string | null;
  researching?: boolean;
  /** Position 1..N (première occurrence) dans la file d’attente empereur. */
  queuePosition?: number | null;
  /** Nombre d’entrées de cette techno dans la file (niveaux suivants). */
  queueCount?: number | null;
  /** Peut être ajoutée à la file (recherche en cours + slots libres). */
  canEnqueue?: boolean;
};

export type TechnologyActiveResearch = {
  id: number;
  technologyId: string;
  targetLevel: number;
  costs: Record<string, number>;
  startedAt: string | null;
  completesAt: string | null;
  remainingSeconds: number;
  durationLabel: string;
};

export type TechnologyWaitlistItem = {
  id: number;
  technologyId: string;
  targetLevel: number;
  position: number;
  costs: Record<string, number>;
  createdAt: string | null;
  /** Durée estimée au démarrage (scientifiques inclus). */
  durationSeconds?: number | null;
  durationLabel?: string | null;
};

export type TechnologyTree = {
  version: number;
  categories: Array<{
    id: TechnologyCategoryId;
    name: string;
    icon: string;
    branches: string[];
  }>;
  definitions: TechnologyNode[];
  balances: Record<string, number>;
  inventory: Record<string, number>;
  limits: Record<string, unknown>;
  automation: Record<string, unknown>;
  adminPreview?: boolean;
  activeResearch?: TechnologyActiveResearch | null;
  isEmperor?: boolean;
  researchWaitlist?: TechnologyWaitlistItem[];
  researchWaitlistMax?: number;
  specialists?: {
    slots: number;
    slotsUsed: number;
    assignHint?: string;
    kinds: Record<
      string,
      { owned: number; assigned: number; reserve: number; scope: 'campus' | 'territory' | string }
    >;
    investorPlacements?: Array<{
      territoryId: number;
      name: string;
      centroid?: { lng: number; lat: number } | null;
    }>;
  };
  materials?: { steel: number; concrete: number };
};

export type TechnologyPurchaseResult = {
  ok: boolean;
  duplicate?: boolean;
  technologyId?: string;
  level?: number;
  cost?: number;
  resource?: string;
  balance?: number;
  influenceStateVersion?: number;
  recalculationPending?: boolean;
  error?: string;
  code?: string;
};

export type TechnologyUseResult = {
  ok: boolean;
  duplicate?: boolean;
  technologyId?: string;
  effectType?: string;
  chargesGranted?: number;
  wallet?: Wallet;
  influenceStateVersion?: number;
  recalculationPending?: boolean;
  error?: string;
  code?: string;
};

export const techApi = {
  async tree() {
    return apiFetch<TechnologyTree>('technologies.php?action=tree');
  },

  async activateMartialLaw(territoryId: number, idempotencyKey: string) {
    return apiFetch<{ ok: boolean; duplicate?: boolean; error?: string } & Partial<MartialLaw>>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'activate_martial_law', territoryId, idempotencyKey }),
    });
  },

  async purchase(technologyId: string, idempotencyKey: string) {
    return apiFetch<
      TechnologyPurchaseResult & {
        research?: boolean;
        queued?: boolean;
        position?: number | null;
        durationLabel?: string;
        activeResearch?: TechnologyActiveResearch;
        researchWaitlist?: TechnologyWaitlistItem[];
      }
    >('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'purchase', technologyId, idempotencyKey }),
    });
  },

  async cancelResearch() {
    return apiFetch<{
      ok: boolean;
      cancelled?: boolean;
      refunded?: Record<string, number>;
      activeResearch?: TechnologyActiveResearch | null;
      researchWaitlist?: TechnologyWaitlistItem[];
      error?: string;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'cancel_research' }),
    });
  },

  async removeResearchWaitlist(opts: { waitlistId?: number; technologyId?: string }) {
    return apiFetch<{
      ok: boolean;
      removed?: boolean;
      technologyId?: string;
      refunded?: Record<string, number>;
      activeResearch?: TechnologyActiveResearch | null;
      researchWaitlist?: TechnologyWaitlistItem[];
      error?: string;
      code?: string;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'remove_waitlist',
        waitlistId: opts.waitlistId ?? 0,
        technologyId: opts.technologyId ?? '',
      }),
    });
  },

  async assignSpecialist(kind: string, delta: 1 | -1) {
    return apiFetch<{
      ok: boolean;
      kind?: string;
      assigned?: number;
      specialists?: TechnologyTree['specialists'];
      error?: string;
      code?: string;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'assign_specialist', kind, delta }),
    });
  },

  async assignTerritorySpecialist(kind: string, territoryId: number, delta: 1 | -1) {
    return apiFetch<{
      ok: boolean;
      kind?: string;
      territoryId?: number;
      assigned?: number;
      investors?: {
        owned: number;
        assigned: number;
        reserve: number;
        zoneAssigned: number;
        ecoBonusPercent: number;
        buildCutPercent: number;
        canAssign: boolean;
        canRemove: boolean;
        governs: boolean;
        inZone: boolean;
      } | null;
      specialists?: TechnologyTree['specialists'];
      error?: string;
      code?: string;
    }>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'assign_territory_specialist',
        kind,
        territoryId,
        delta,
      }),
    });
  },

  async use(technologyId: string, idempotencyKey: string, targetId?: number) {
    return apiFetch<TechnologyUseResult>('technologies.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'use',
        technologyId,
        idempotencyKey,
        ...(targetId != null ? { targetId } : {}),
      }),
    });
  },
};

// ─── Bâtiments ───────────────────────────────────────────────────────────────

export type BuildingTypeCode =
  | 'arms_factory'
  | 'steel_foundry'
  | 'concrete_plant'
  | 'bank'
  | 'tourism_office'
  | 'fort'
  | 'outpost'
  | 'university'
  | 'business_district'
  | 'port'
  | 'shrine'
  | 'museum'
  | 'airport';

export type BuildingState =
  | 'under_construction'
  | 'operational'
  | 'damaged'
  | 'inactive_capacity'
  | 'inactive_governor'
  | 'inactive_condition'
  | 'suspended'
  | 'destroyed';

export type BuildingTypeInfo = {
  code: BuildingTypeCode;
  name: string;
  category: string;
  icon: string;
  costs: Record<string, number>;
  durationSeconds: number;
  parcelCount: number;
  requiresSpacing: boolean;
  requiresCoastal?: boolean;
  isMain: boolean;
  technologyId: string;
  repairCosts: Record<string, number>;
};

export type BuildingBankInfo = {
  balance: number;
  capacity: number;
  ratePerHour: number;
  ratePercentPerHour: number;
  /** Intérêts / h versés en production économique (pas capitalisés sur le dépôt). */
  interestPerHour?: number;
  hasAdjacentQda: boolean;
  canOperate: boolean;
  lifetimeInterest: number;
  seizeOnConquerPercent: number;
  withdrawFeePercent: number;
};

export type BuildingRecord = {
  id: number;
  type: BuildingTypeCode;
  name?: string;
  icon?: string;
  category?: string;
  ownerUserId: number;
  creatorUserId: number;
  territoryId: number | null;
  territoryName?: string;
  lng?: number;
  lat?: number;
  state: BuildingState;
  isDamaged: boolean;
  activationPriority: number;
  startedAt: string | null;
  completesAt: string | null;
  completedAt: string | null;
  remainingSeconds: number | null;
  paidCosts: Record<string, number> | null;
  requiresCoastal?: boolean;
  portRangeKm?: number;
  bank?: BuildingBankInfo | null;
  parcels?: Array<{
    id: number;
    territoryId: number;
    territoryName?: string;
    lng?: number;
    lat?: number;
    index: number;
    state: string;
    isDamaged: boolean;
    completesAt: string | null;
    remainingSeconds: number | null;
  }>;
  parcelIndex?: number;
  parcelState?: string;
};

export const buildingsApi = {
  catalog() {
    return apiFetch<{ ok: boolean; types: BuildingTypeInfo[] }>('buildings.php?action=catalog');
  },
  mine() {
    return apiFetch<{
      ok: boolean;
      buildings: BuildingRecord[];
      stats: Record<string, { capacity: number; used: number }>;
      effects: Record<string, unknown>;
    }>('buildings.php?action=mine');
  },
  map(bbox: string) {
    return apiFetch<{
      type: 'FeatureCollection';
      features: Array<{
        type: string;
        geometry: { type: string; coordinates: [number, number] };
        properties: Record<string, unknown>;
      }>;
    }>(`buildings.php?action=map&bbox=${encodeURIComponent(bbox)}`);
  },
  territory(territoryId: number) {
    return apiFetch<{ ok: boolean; territoryId: number; buildings: BuildingRecord[] }>(
      `buildings.php?action=territory&territoryId=${territoryId}`,
    );
  },
  eligibility(type: BuildingTypeCode, territoryId: number, existingTerritoryIds: number[] = []) {
    const existing =
      existingTerritoryIds.length > 0 ? `&existing=${existingTerritoryIds.join(',')}` : '';
    return apiFetch<Record<string, unknown>>(
      `buildings.php?action=eligibility&type=${type}&territoryId=${territoryId}${existing}`,
    );
  },
  constructibles(territoryId: number, airportExisting: number[] = []) {
    const existing =
      airportExisting.length > 0 ? `&existing=${airportExisting.join(',')}` : '';
    return apiFetch<{
      ok: boolean;
      territoryId: number;
      governorUserId: number | null;
      isOwn: boolean;
      isEnemy: boolean;
      constructibles: Array<{
        code: BuildingTypeCode;
        name: string;
        category: string;
        icon: string;
        costs: Record<string, number>;
        durationSeconds: number;
        parcelCount: number;
        canBuild: boolean;
        blockCode: string | null;
        blockMessage: string | null;
        nearbyBuildingName: string | null;
      }>;
      airportPicks: number[];
    }>(`buildings.php?action=constructibles&territoryId=${territoryId}${existing}`);
  },
  preview(type: BuildingTypeCode, territoryId: number, existingTerritoryIds: number[] = []) {
    const existing =
      existingTerritoryIds.length > 0 ? `&existing=${existingTerritoryIds.join(',')}` : '';
    return apiFetch<{
      ok: boolean;
      eligibility: Record<string, unknown>;
      boostedPois: Array<Record<string, unknown>>;
      reinforcedNeighbors: number[];
    }>(`buildings.php?action=preview&type=${type}&territoryId=${territoryId}${existing}`);
  },
  construct(type: BuildingTypeCode, territoryId: number, idempotencyKey: string) {
    return apiFetch<{
      ok: boolean;
      buildingId?: number;
      error?: string;
      code?: string;
      parcel?: boolean;
      formedAirport?: { buildingId: number; territoryIds: number[]; state: string } | null;
    }>('buildings.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'construct', type, territoryId, idempotencyKey }),
    });
  },
  constructAirport(territoryId: number, idempotencyKey: string) {
    return apiFetch<{
      ok: boolean;
      buildingId?: number;
      error?: string;
      formedAirport?: { buildingId: number; territoryIds: number[]; state: string } | null;
    }>('buildings.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'constructAirport',
        territoryId,
        idempotencyKey,
      }),
    });
  },
  destroy(buildingId: number) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      influencePending?: boolean;
      recalculationPending?: boolean;
      bankRefunded?: number;
    }>('buildings.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'destroy', buildingId }),
    });
  },
  bankDeposit(buildingId: number, amount: number, idempotencyKey: string) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      deposited?: number;
      bank?: BuildingBankInfo;
      balances?: Record<string, number>;
    }>('buildings.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'bankDeposit', buildingId, amount, idempotencyKey }),
    });
  },
  bankWithdraw(buildingId: number, amount: number, idempotencyKey: string) {
    return apiFetch<{
      ok: boolean;
      error?: string;
      code?: string;
      withdrawn?: number;
      fee?: number;
      credited?: number;
      bank?: BuildingBankInfo;
      balances?: Record<string, number>;
    }>('buildings.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'bankWithdraw', buildingId, amount, idempotencyKey }),
    });
  },
  repair(buildingId: number, idempotencyKey: string, parcelId?: number) {
    return apiFetch<{ ok: boolean; error?: string; costs?: Record<string, number> }>('buildings.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'repair',
        buildingId,
        idempotencyKey,
        ...(parcelId != null ? { parcelId } : {}),
      }),
    });
  },
  setPriorities(type: BuildingTypeCode, orderedBuildingIds: number[]) {
    return apiFetch<{ ok: boolean }>('buildings.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'setPriorities', type, orderedBuildingIds }),
    });
  },
  adminStats() {
    return apiFetch<{
      ok: boolean;
      byTypeState: Array<{ type_code: string; state: string; c: number }>;
      airports: number;
    }>('buildings.php?action=admin_stats');
  },
};


export type MarketAsset = 'science' | 'military' | 'economy' | 'spirituality' | 'culture' | 'charges';

export type MarketAuction = {
  id: number;
  seller_user_id: number;
  seller_name?: string;
  offer_asset: MarketAsset;
  offer_amount: string | number;
  ask_asset: MarketAsset;
  start_price: string | number;
  current_price: string | number;
  current_bidder_id: number | null;
  bidder_name?: string | null;
  bid_count: number;
  status: string;
  advised_price: string | number | null;
  fee_percent: string | number;
  fee_amount?: string | number | null;
  net_amount?: string | number | null;
  created_at: string;
  closes_at: string;
  closed_at?: string | null;
};

export type ChargeContractOffer = {
  id: number;
  lender_user_id: number;
  lender_name?: string;
  advance_charges: number;
  total_due: number;
  premium_charges: number;
  status: string;
  created_at: string;
  expires_at: string;
};

export type ChargeContract = {
  id: number;
  offer_id: number;
  lender_user_id: number;
  borrower_user_id: number;
  lender_name?: string;
  borrower_name?: string;
  advance_charges: number;
  total_due: number;
  paid_charges: number;
  status: string;
  activated_at: string;
  theoretical_end_at: string;
  completed_at?: string | null;
  overdue_count?: number;
  yieldPercent?: number;
  nextDue?: {
    id: number;
    installment_no: number;
    due_at: string;
    status: string;
  } | null;
  installments?: Array<{
    id: number;
    installment_no: number;
    due_at: string;
    status: string;
    paid_at?: string | null;
    source?: string | null;
  }>;
};

export const marketApi = {
  status() {
    return apiFetch<{
      enabled: boolean;
      adminOnly: boolean;
      isAdmin: boolean;
      config: Record<string, unknown>;
      rules: string;
    }>('market.php?action=status');
  },
  unread() {
    return apiFetch<{
      unreadCount: number;
      auctions: number;
      offers: number;
      lastSeenAt: string | null;
    }>('market.php?action=unread');
  },
  markSeen() {
    return apiFetch<{ ok: boolean; seenAt: string | null; unreadCount: number }>(
      'market.php?action=mark_seen',
      { method: 'POST', body: '{}' },
    );
  },
  economy() {
    return apiFetch<{
      production: Record<string, number>;
      sharePercent: Record<string, number>;
      totalProduction: number;
      rates: Record<string, Record<string, number>>;
      reliability: string;
      capturedAt: string | null;
      openAuctions: number;
      volume24h: { count: number; gross: number };
      rarest: string | null;
      mostAbundant: string | null;
      feePercent: number;
      rules: string;
      adminOnly: boolean;
      config: Record<string, unknown>;
    }>('market.php?action=economy');
  },
  advisedPrice(offerAsset: string, offerAmount: number, askAsset: string) {
    const q = new URLSearchParams({
      action: 'advised_price',
      offerAsset,
      offerAmount: String(offerAmount),
      askAsset,
    });
    return apiFetch<{
      price: number | null;
      reliability: string;
      low: number | null;
      high: number | null;
    }>(`market.php?${q}`);
  },
  auctions(filters?: { offerAsset?: string; askAsset?: string }) {
    const q = new URLSearchParams({ action: 'auctions' });
    if (filters?.offerAsset) q.set('offerAsset', filters.offerAsset);
    if (filters?.askAsset) q.set('askAsset', filters.askAsset);
    return apiFetch<{ auctions: MarketAuction[] }>(`market.php?${q}`);
  },
  myAuctions() {
    return apiFetch<{ auctions: MarketAuction[] }>('market.php?action=my_auctions');
  },
  createAuction(body: {
    offerAsset: string;
    offerAmount: number;
    askAsset: string;
    startPrice: number;
    durationHours: number;
  }) {
    return apiFetch<{ auction: MarketAuction }>('market.php?action=create_auction', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  bid(auctionId: number, amount: number) {
    return apiFetch<{ auction: MarketAuction }>('market.php?action=bid', {
      method: 'POST',
      body: JSON.stringify({ auctionId, amount }),
    });
  },
  cancelAuction(auctionId: number) {
    return apiFetch<{ auction: MarketAuction }>('market.php?action=cancel_auction', {
      method: 'POST',
      body: JSON.stringify({ auctionId }),
    });
  },
  history() {
    return apiFetch<{ transactions: Array<Record<string, unknown>> }>('market.php?action=history');
  },
  chargeOffers() {
    return apiFetch<{ offers: ChargeContractOffer[] }>('market.php?action=charge_offers');
  },
  createChargeOffer(advance: number, durationHours: number) {
    return apiFetch<{ offer: ChargeContractOffer }>('market.php?action=create_charge_offer', {
      method: 'POST',
      body: JSON.stringify({ advance, durationHours }),
    });
  },
  cancelChargeOffer(offerId: number) {
    return apiFetch<{ offer: ChargeContractOffer }>('market.php?action=cancel_charge_offer', {
      method: 'POST',
      body: JSON.stringify({ offerId }),
    });
  },
  acceptChargeOffer(offerId: number) {
    return apiFetch<{ contract: ChargeContract; wallet: Wallet }>('market.php?action=accept_charge_offer', {
      method: 'POST',
      body: JSON.stringify({ offerId }),
    });
  },
  myContracts() {
    return apiFetch<{ contracts: ChargeContract[] }>('market.php?action=my_contracts');
  },
  contract(id: number) {
    return apiFetch<{ contract: ChargeContract }>(`market.php?action=contract&id=${id}`);
  },
  adminOverview() {
    return apiFetch<Record<string, unknown>>('market.php?action=admin_overview');
  },
  adminAuctions() {
    return apiFetch<{ auctions: MarketAuction[] }>('market.php?action=admin_auctions');
  },
  adminTransactions(opts?: { limit?: number; offset?: number }) {
    const q = new URLSearchParams({ action: 'admin_transactions' });
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    if (opts?.offset != null) q.set('offset', String(opts.offset));
    return apiFetch<{
      transactions: Array<Record<string, unknown>>;
      total: number;
      limit: number;
      offset: number;
    }>(`market.php?${q}`);
  },
  adminContracts(status?: string) {
    const q = new URLSearchParams({ action: 'admin_contracts' });
    if (status) q.set('status', status);
    return apiFetch<{ contracts: ChargeContract[] }>(`market.php?${q}`);
  },
  adminRiskFlags() {
    return apiFetch<{ flags: Array<Record<string, unknown>> }>('market.php?action=admin_risk_flags');
  },
  adminRiskFlag(flagId: number) {
    return apiFetch<{
      flag: Record<string, unknown>;
      details: Record<string, unknown>;
      transaction: Record<string, unknown> | null;
      auction: Record<string, unknown> | null;
      risk: {
        score: number;
        computedScore: number;
        pairCount7d: number;
        triggerReasons: Array<{ code: string; meta: Record<string, unknown> }>;
        scoreReasons: Array<{ code: string; points: number; meta: Record<string, unknown> }>;
      };
    }>(`market.php?action=admin_risk_flag&id=${flagId}`);
  },
  adminCloseContract(contractId: number, reason: string) {
    return apiFetch<{ contract: ChargeContract }>('market.php?action=admin_close_contract', {
      method: 'POST',
      body: JSON.stringify({ contractId, reason }),
    });
  },
  adminReviewFlag(flagId: number, status: string) {
    return apiFetch<{ ok: boolean }>('market.php?action=admin_review_flag', {
      method: 'POST',
      body: JSON.stringify({ flagId, status }),
    });
  },
  adminRefreshPrices() {
    return apiFetch<Record<string, unknown>>('market.php?action=admin_refresh_prices', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
};
